import { ITEMTYPE } from "../gamedata/inventorydata";
import {
  decodeStatOption,
  collapseAllStats,
  decodeRequirement,
  DecodedStat,
  DecodedRequirement,
  PlayerStatsForRequirement,
  StatType,
} from "../gamedata/itemstattypes";

// Matches the server's per-item dodge cap, see worldclient.ts's MAX_ITEM_DODGE.
const MAX_ITEM_DODGE = 45;

// Matches the server's GetDodge() item loop (worldclient.ts): current
// durability contributes floor(durability*0.3), plus an explicit Dodge stat
// roll if the item has one, capped at MAX_ITEM_DODGE.
function getItemDodge(durability: number, stats: any): number {
  let dodge = Math.floor(durability * 0.3);
  if (stats.statType1 === StatType.DODGE || stats.statType1 === StatType.DODGE_ALT) {
    dodge += stats.statValue1;
  }
  if (stats.statType2 === StatType.DODGE || stats.statType2 === StatType.DODGE_ALT) {
    dodge += stats.statValue2;
  }
  return Math.min(MAX_ITEM_DODGE, dodge);
}

// ponytail: no authoritative item-grade tier name/color table exists
// anywhere in server/OriginalServerFiles or rose-tools -- this project's
// own LIST_WEAPON.STB was inspected directly and itemgrade values 0-9
// were observed. This gradient is a best-effort guess; confirm against
// live items and adjust the thresholds/colors here once seen in-game.
const GRADE_TIERS: { max: number; name: string; color: string }[] = [
  { max: 1, name: "Normal", color: "#ffffff" },
  { max: 3, name: "Rare", color: "#3fdb5c" },
  { max: 5, name: "Advanced", color: "#4fa3ff" },
  { max: 7, name: "Unique", color: "#c060f0" },
  { max: Infinity, name: "Legendary", color: "#ffa236" },
];

function getGradeTier(itemGrade: number) {
  return GRADE_TIERS.find((t) => itemGrade <= t.max) || GRADE_TIERS[GRADE_TIERS.length - 1];
}

// ponytail: no weapon-subtype id->name table exists anywhere in the
// codebase or the original server source -- this is a best-guess standard
// ROSE Online weapon category list, not verified against this project's
// own item data. Confirm against known weapons live and correct any
// mismatches.
const WEAPON_TYPE_NAMES: Record<number, string> = {
  0: "Bare Fist",
  1: "Sword",
  2: "Two-Handed Sword",
  3: "Spear",
  4: "Axe",
  5: "Two-Handed Axe",
  6: "Bow",
  7: "Gun",
  8: "Launcher",
  9: "Staff",
  10: "Katar",
};

function getWeaponTypeName(subtype: number): string {
  return WEAPON_TYPE_NAMES[subtype] || `Type ${subtype}`;
}

function isEquipFamily(itemType: number): boolean {
  return itemType >= ITEMTYPE.FACE_ITEM && itemType <= ITEMTYPE.SHIELD;
}

function statCell(label: string, value: any): string {
  return `<div class="stat-cell">${label}:${value}</div>`;
}

class _TooltipManager {
  itemData: any;
  skillData: any;
  _tooltipElement: any;
  _disableTooltips: boolean;
  _expandTooltips: boolean;

  constructor() {
    this.itemData = null;
    this.skillData = null;
    this._tooltipElement = null;
    this._disableTooltips = false;
    this._expandTooltips = false;

    $(document).on("mousedown", this._onGlobalMouseDown.bind(this));
    $(document).on("mouseup", this._onGlobalMouseUp.bind(this));
  }

  _onGlobalMouseDown(downEvent: any) {
    if (downEvent.which === 1) {
      this._disableTooltips = true;
      this.hideTooltip();
    } else if (downEvent.which === 3) {
      this._expandTooltips = true;
    }
  }

  _onGlobalMouseUp(upEvent: any) {
    if (upEvent.which === 1) {
      this._disableTooltips = false;
    } else if (upEvent.which === 3) {
      this._expandTooltips = false;
    }
  }

  showTooltip(tooltip: any, x: any, y: any) {
    if (!this._tooltipElement) {
      this._tooltipElement = $('<div class="tooltip" />');
      $(".ui").append(this._tooltipElement);
    }

    if (!this._disableTooltips) {
      this._tooltipElement.html(tooltip);
      this._tooltipElement.show();
      this.moveTooltip(x, y);
    }
  }

  moveTooltip(x: any, y: any) {
    const width = this._tooltipElement.outerWidth();
    const height = this._tooltipElement.outerHeight();
    x = Math.max(0, Math.min(x, window.innerWidth - width));
    y = Math.max(0, Math.min(y, window.innerHeight - height));
    this._tooltipElement.offset({ left: x + 1, top: y + 1 });
  }

  hideTooltip() {
    if (this._tooltipElement) this._tooltipElement.hide();
  }

  addItemName(item: any, html: any) {
    let name = this.itemData.getName(item.itemType, item.itemNo);
    const refine = item.refineGrade;
    let colorStyle = "";
    if (isEquipFamily(item.itemType)) {
      const stats = this.itemData.getEquipStats(item.itemType, item.itemNo);
      const tier = getGradeTier(stats.itemGrade);
      colorStyle = ` style="color: ${tier.color}"`;
    }
    html += `<div class="item name"${colorStyle}>`;
    html += name;
    if (refine > 0) {
      html += " (" + refine + ")";
    }
    html += "</div>";
    return html;
  }

  addItemDescription(item: any, html: any) {
    let desc = this.itemData.getDescription(item.itemType, item.itemNo);
    html += '<div class="item description">';
    html += desc;
    html += "</div>";
    return html;
  }

  _playerStatsForRequirement(): PlayerStatsForRequirement {
    return {
      level: MC.level,
      str: MC.stats.str,
      dex: MC.stats.dex,
      int: MC.stats.int,
      con: MC.stats.con,
      cha: MC.stats.cha,
      sen: MC.stats.sen,
    };
  }

  addRequirements(stats: any, html: any) {
    const player = this._playerStatsForRequirement();
    const reqs = [
      decodeRequirement(stats.reqType1, stats.reqValue1, player),
      decodeRequirement(stats.reqType2, stats.reqValue2, player),
    ].filter((r): r is DecodedRequirement => r !== null);
    reqs.forEach((req) => {
      html += `<div class="item requirement ${req.met ? "met" : "unmet"}">`;
      html += `Requires: ${req.label} ${req.required}`;
      html += "</div>";
    });
    return html;
  }

  // "Intelligence +5" lines for a LIST_JEMITEM row's stat1/stat2.
  _gemStatLines(gemNo: number): string {
    const gem = this.itemData.getGemStats(gemNo);
    return collapseAllStats(
      [
        decodeStatOption(gem.statType1, gem.statValue1),
        decodeStatOption(gem.statType2, gem.statValue2),
      ].filter((s): s is DecodedStat => s !== null)
    )
      .map((s) => `${s.label} +${s.value}${s.isPercent ? "%" : ""}`)
      .join("<br>");
  }

  // One socket per item (server CItem.socketed/gem): the socketed gem's
  // name and bonus stats, or an empty-socket line.
  addSocket(item: any, html: any) {
    if (!(item.socketCount > 0)) return html;
    const gemNo = item.gemOption1 || 0;
    if (!gemNo) {
      return html + '<div class="item socket-empty">Empty Socket</div>';
    }
    html += '<div class="item gem">';
    html += `<span class="gem-name">${this.itemData.getName(ITEMTYPE.GEM, gemNo)}</span><br>`;
    html += this._gemStatLines(gemNo);
    html += "</div>";
    return html;
  }

  addEquipStats(item: any, html: any) {
    const stats = this.itemData.getEquipStats(item.itemType, item.itemNo);
    const isWeapon = item.itemType === ITEMTYPE.WEAPON || item.itemType === ITEMTYPE.SHIELD;
    // Matches the server's GetDodge() item loop (worldclient.ts): face items
    // and weapons grant no dodge.
    const grantsDodge = !isWeapon && item.itemType !== ITEMTYPE.FACE_ITEM;
    // Catalog entries (GM item browser, vending lists) carry no durability;
    // show them as a fresh item at max, which is what the server grants.
    const durability = item.durability ?? stats.quality;

    html += '<div class="stat-grid">';
    if (isWeapon) {
      html += statCell("Attack Power", stats.attackPower);
      html += statCell("Attack Distance", stats.attackDistance + "M");
    } else {
      html += statCell("Defense", stats.defense);
      html += statCell("Magic Resistance", stats.magicResist);
    }
    if (grantsDodge) {
      html += statCell("Dodge", getItemDodge(durability, stats));
    }
    // ponytail: the STB calls this column "quality", not "maxdurability",
    // but no separate max-durability field exists anywhere in CEquip --
    // treating it as the item's max durability for "current/max" display.
    html += statCell("Durability", durability + "/" + stats.quality);
    html += "</div>";

    if (isWeapon) {
      html += '<div class="item stat-single">Attacks/Second:' + stats.attackSpeed + "</div>";
      html += '<div class="item stat-single">Type:' + getWeaponTypeName(stats.subtype) + "</div>";
    }

    const tier = getGradeTier(stats.itemGrade);
    html += '<div class="item stat-single">Item Grade:' + tier.name + "</div>";

    // DODGE/DODGE_ALT are excluded here when grantsDodge already folded them
    // into the computed Dodge stat cell above -- shown once, not twice.
    const bracketStats = collapseAllStats(
      [
        decodeStatOption(stats.statType1, stats.statValue1),
        decodeStatOption(stats.statType2, stats.statValue2),
      ].filter(
        (s): s is DecodedStat =>
          s !== null && !(grantsDodge && (s.type === StatType.DODGE || s.type === StatType.DODGE_ALT))
      )
    );
    if (bracketStats.length) {
      html += '<div class="item stat-bracket">';
      html += bracketStats
        .map((s) => `[${s.label} ${s.value}${s.isPercent ? "%" : ""}]`)
        .join("<br>");
      html += "</div>";
    }

    html = this.addSocket(item, html);
    html = this.addRequirements(stats, html);

    html += '<div class="item stat-single">Weight:' + stats.weight + "</div>";

    const value = this.itemData.getSellValue(item.itemType, item.itemNo, item.durability || 0);
    if (value !== null) {
      html += '<div class="item value">Player Value:' + value.toLocaleString() + "</div>";
    }

    return html;
  }

  addConsumableStats(item: any, html: any) {
    const stats = this.itemData.getConsumableStats(item.itemNo);
    html += '<div class="item stat-single">Weight:' + stats.weight + "</div>";
    if (item.quantity > 1) {
      html += '<div class="item stat-single">Quantity:' + item.quantity + "</div>";
    }
    return html;
  }

  addNaturalStats(item: any, html: any) {
    const stats = this.itemData.getNaturalStats(item.itemNo);
    html += '<div class="item stat-single">Weight:' + stats.weight + "</div>";
    return html;
  }

  addPatStats(item: any, html: any) {
    const stats = this.itemData.getPatStats(item.itemNo);
    html += '<div class="stat-grid">';
    html += statCell("Attack Power", stats.attackPower);
    html += statCell("Attack Distance", stats.attackDistance + "M");
    html += "</div>";
    html += '<div class="item stat-single">Weight:' + stats.weight + "</div>";

    const value = this.itemData.getSellValue(item.itemType, item.itemNo, item.durability || 0);
    if (value !== null) {
      html += '<div class="item value">Player Value:' + value.toLocaleString() + "</div>";
    }

    return html;
  }

  addFooter(html: any) {
    html += '<div class="item footer">';
    html += "Press Ctrl + Right Click to preview item<br>";
    html += "Press Ctrl + Left Click to add to Buy List<br>";
    html += "Press Shift + Right Click to link in chat";
    html += "</div>";
    return html;
  }

  getItemTooltip(item: any) {
    if (!this.itemData) {
      this.itemData = GDM.getNow("item_data");
    }

    let html = "<div>";
    html = this.addItemName(item, html);

    if (isEquipFamily(item.itemType)) {
      html = this.addEquipStats(item, html);
    } else if (item.itemType === ITEMTYPE.USE) {
      html = this.addConsumableStats(item, html);
    } else if (item.itemType === ITEMTYPE.GEM && item.itemNo > 300) {
      html += '<div class="item stat-special">' + this._gemStatLines(item.itemNo) + "</div>";
    } else if (item.itemType === ITEMTYPE.NATURAL) {
      html = this.addNaturalStats(item, html);
    } else if (item.itemType === ITEMTYPE.RIDE_PART) {
      html = this.addPatStats(item, html);
    }

    html = this.addItemDescription(item, html);
    html = this.addFooter(html);
    html += "</div>";
    return $(html);
  }

  getCommandTooltip(command: any) {}

  addSkillName(skill: any, html: any) {
    const name = this.skillData.getName(skill.skillIdx);
    html += '<div class="skill name">';
    html += name;
    html += "</div>";
    return html;
  }

  addSkillDescription(skill: any, html: any) {
    const desc = this.skillData.getDescription(skill.skillIdx);
    html += '<div class="skill description">';
    html += desc;
    html += "</div>";
    return html;
  }

  getSkillTooltip(skill: any) {
    if (!this.skillData) {
      this.skillData = GDM.getNow("skill_data");
    }

    let html = "<div>";
    html = this.addSkillName(skill, html);
    html = this.addSkillDescription(skill, html);
    html += "</div>";
    return $(html);
  }

  getEmoteTooltip(emote: any) {}

  getDialogTooltip(dialog: any) {}

  getClanSkillTooltip(skill: any) {}
}

export const TooltipManager = new _TooltipManager();
